import { keys as localDbKeys } from "idb-keyval";
import { API } from "aws-amplify";
import {
  get as localDbGet,
  del as localDbDelete,
} from "idb-keyval";
import { makeImageLocalKey } from "helpers/imageHelpers";
import { getUserAndEnvFromLocalStorage } from "utils";
import { CloudWatchLogger } from "helpers/cloudWatchLogger";

const BYTES_IN_KILOBYTE = 1024;
const STANDARD_IMAGE_SIZE_KB = 30;
const STANDARD_IMAGE_UPLOAD_TIME_MILLISECONDS = 4500;
const UNDEFINED_DATA_URI_ERROR = "dataUri undefined";
const MAX_RETRIES = 12;

class ImageOutbox {
  processing = false;
  totalPending = 0;
  _lock = false;

  constructor() {
    if (ImageOutbox._instance) {
      return ImageOutbox._instance;
    }

    ImageOutbox._instance = this;
    this._lock = false;
    this.start();
  }

  async start() {
    this.resume();
  }

  async getPendingImageIds() {
    const allLocalDbKeys = await localDbKeys();

    return allLocalDbKeys.filter((key) => key.startsWith("mutationv2"));
  }

  async getPendingImageTotal() {
    const pendingImagesIds = await this.getPendingImageIds();
    const total = pendingImagesIds.length;
    this.totalPending = total;
    return total;
  }

  async uploadImage(
    currentPendingImageKey,
    key,
    tenantId,
    facilityId,
    dataUri,
    retryCount = 0
  ) {
    const { env, user } = await getUserAndEnvFromLocalStorage();

    let itemUploaded = false;
    let continueUpload = true;

    const itemId = key.split("/")[0]
    const fileName = key.split("/")[1]

    const estimatedUploadTime = this.getEstimatedUploadTime(dataUri);
    const t0 = performance.now();

    let t1 = null;
    const loggerData = {
      itemId,
      fileName,
      tenantId,
      facilityId,
      estimatedUploadTime,
      retryCount,
      imageOutboxSize: this.totalPending
    };

    try {
      await API.post("rest", "/items/image", {
        body: {
          tenantId,
          key: fileName,
          dataUri,
          id: itemId,
        },
        response: false,
      });

      itemUploaded = true;
      t1 = performance.now();

      await localDbDelete(currentPendingImageKey);

      CloudWatchLogger(
        `/aws/lambda/rest-${env}`,
        "info",
        `${user} imageOutbox: Image successfully uploaded to S3.`,
        loggerData
      );

      this.totalPending = Math.max(0, this.totalPending - 1);

      if (t1 - t0 > estimatedUploadTime) {
        continueUpload = false;

        CloudWatchLogger(
          `/aws/lambda/rest-${env}`,
          "info",
          `${user} imageOutbox: Slow connection detected. Pausing upload for later.`,
          loggerData
        );
      }
    } catch (e) {
      continueUpload = false;

      const errorLogData = {
        ...loggerData,
        errorDetails: {
          error: e?.message,
          code: e?.code,
          statusCode: e?.statusCode,
          requestId: e?.requestId,
          stack: e?.stack,
        },
      };

      // Handle network errors with retry logic
      if (e.message === "Network Error" && retryCount < MAX_RETRIES) {
        CloudWatchLogger(
          `/aws/lambda/rest-${env}`,
          "warn",
          `${user} imageOutbox: Network error uploading ${itemId} image. Retrying (${
            retryCount + 1
          }/${MAX_RETRIES}).`,
          errorLogData
        );

        // Exponential backoff: wait 1s, 2s, 4s for retries, with a cap of 60s
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.min(60, Math.pow(2, retryCount)))
        );

        return this.uploadImage(
          currentPendingImageKey,
          key,
          tenantId,
          facilityId,
          dataUri,
          retryCount + 1
        );
      }

      // Log final failure
      CloudWatchLogger(
        `/aws/lambda/rest-${env}`,
        "error",
        `${user} imageOutbox: Failed to upload ${itemId} image after ${
          retryCount + 1
        } attempts. S3 responded with the following error: ${e}.`,
        errorLogData
      );

      // For network errors after max retries, throw to halt queue processing
      // This prevents burning through the entire queue when there's a persistent connectivity issue
      if (e.message === "Network Error") {
        throw e;
      }

      // For other errors (auth, validation, etc.), continue processing next image
      // This image will remain in queue and can be retried later
    }

    return { itemUploaded, continueUpload };
  }

  async resume() {
    if (this.processing || this._lock) {
      return;
    }

    this._lock = true;

    try {
      const { env, user } = await getUserAndEnvFromLocalStorage();

      CloudWatchLogger(
        `/aws/lambda/rest-${env}`,
        "info",
        `${user} imageOutbox: Image processing has been RESUMED`,
        {
          imageOutboxSize: this.totalPending
        }
      );

      await this._processQueue();
    } finally {
      this._lock = false;
    }
  }

  async _processQueue() {
    const { env, user } = await getUserAndEnvFromLocalStorage();

    await this.getPendingImageTotal();

    this.processing = true;

    let pendingImagesIds = await this.getPendingImageIds();

    if (!pendingImagesIds || !pendingImagesIds.length) {
      this.pause();
      return;
    }

    do {
      const currentPendingImageId = pendingImagesIds[0];

      CloudWatchLogger(
        `/aws/lambda/rest-${env}`,
        "info",
        `${user} imageOutbox: Processing image: ${currentPendingImageId}.`,
        {
          imageOutboxSize: this.totalPending
        }
      );

      try {
        const migrationResult = await this._processV2Mutation(
          currentPendingImageId
        );

        if (!migrationResult.continueUpload) {
          this.pause();
          break;
        }
      } catch (e) {
        if (e.message === UNDEFINED_DATA_URI_ERROR) {
          // We can't find image locally so delete and forget about it
          CloudWatchLogger(
            `/aws/lambda/rest-${env}`,
            "error",
            `${user} imageOutbox: invalid image URL saved for ${currentPendingImageId}, ignoring it`,
            { currentPendingImageId, error: e?.message, stack: e?.stack }
          )
          await localDbDelete(currentPendingImageId);
          this.totalPending = Math.max(0, this.totalPending - 1);
        } else if (e.message === "Network Error") {
          this.pause();
          break;
        } else {
          // Log unexpected errors but continue processing
          CloudWatchLogger(
            `/aws/lambda/rest-${env}`,
            "error",
            `${user} imageOutbox: Unexpected error processing ${currentPendingImageId}: ${e.message}`,
            { currentPendingImageId, error: e?.message, stack: e?.stack }
          );
        }
      }

      // Get all remaining pending images
      pendingImagesIds = await this.getPendingImageIds();
    } while (pendingImagesIds.length > 0 && this.processing);

    this.pause();
  }

  async _processV2Mutation(currentPendingImageId) {
    let mutationData;
    try {
      mutationData = JSON.parse(await localDbGet(currentPendingImageId));
    } catch (e) {
      throw new Error(`Failed to parse v2 mutation data: ${e.message}`);
    }

    const { facilityId, key, hash, tenantId } = mutationData;

    const currentPendingImageKey = makeImageLocalKey({
      type: "pending",
      hash,
      facilityId,
    });

    const dataUri = await localDbGet(currentPendingImageKey);
    if (!dataUri) {
      throw new Error(UNDEFINED_DATA_URI_ERROR);
    }

    const { continueUpload, itemUploaded } = await this.uploadImage(
      currentPendingImageKey,
      key,
      tenantId,
      facilityId,
      dataUri
    );

    // If image has uploaded successfully, delete the mutation record
    if (itemUploaded) {
      await localDbDelete(currentPendingImageId);
      this.totalPending = Math.max(0, this.totalPending - 1);
    }

    return { continueUpload, itemUploaded };
  }

  getEstimatedUploadTime(dataUri) {
    // Minimum upload speed of 1Mbps should allow 30kb of data to be uploaded in 4500ms
    const dataUriUploadSize = new Blob([dataUri]).size / BYTES_IN_KILOBYTE;
    const estimatedUploadTime = Math.ceil(
      (dataUriUploadSize / STANDARD_IMAGE_SIZE_KB) *
        STANDARD_IMAGE_UPLOAD_TIME_MILLISECONDS
    );

    return estimatedUploadTime;
  }

  async pause() {
    const { env, user } = await getUserAndEnvFromLocalStorage();

    this.processing = false;

    CloudWatchLogger(
      `/aws/lambda/rest-${env}`,
      "info",
      `${user} imageOutbox: Image processing has been PAUSED`,
      {
        imageOutboxSize: this.totalPending
      }
    );
  }
}

export default ImageOutbox;
